/**
 * @author: lv.chen
 * @date: 2026/3/13
 * @desc: Select 组件演示文件，展示各种配置效果
 */
import React, { useState } from 'react';
import Select from './index';

import './demo.scss';

// 模拟图标（实际使用时替换为真实图标）
const SensorTowerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="8" cy="8" r="6" fill="#4A90E2" />
  </svg>
);

const AppAnnieIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="8" cy="8" r="6" fill="#E24A4A" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="8" cy="8" r="6" fill="#34A853" />
  </svg>
);

// 基本使用演示
const BasicDemo: React.FC = () => {
  const [value, setValue] = useState<string>('option1');

  return (
    <div className="dbi-component-select-demo__inline">
      <Select
        label="Source"
        value={value}
        onChange={setValue}
        options={[
          { value: 'option1', text: 'Option 1' },
          { value: 'option2', text: 'Option 2' },
          { value: 'option3', text: 'Option 3' },
        ]}
      />
      <span className="dbi-component-select-demo__value">当前值: {value}</span>
    </div>
  );
};

// 带图标和气泡提示演示
const IconDemo: React.FC = () => {
  const [value, setValue] = useState<string>('sensortower');

  return (
    <div className="dbi-component-select-demo__inline">
      <Select
        label="数据源"
        value={value}
        onChange={setValue}
        tooltipMaxWidth={200}
        options={[
          {
            value: 'sensortower',
            text: 'Sensor Tower',
            icon: <SensorTowerIcon />,
            tooltip: 'Sensor Tower 数据源，提供手游市场下载和收入数据',
          },
          {
            value: 'appannie',
            text: 'data.ai',
            icon: <AppAnnieIcon />,
            tooltip: 'data.ai 数据源，提供应用市场分析和情报',
          },
          {
            value: 'google',
            text: 'Google Play',
            icon: <GoogleIcon />,
            tooltip: 'Google Play 商店数据',
          },
        ]}
      />
      <span className="dbi-component-select-demo__value">当前值: {value}</span>
    </div>
  );
};

// 支持清除演示
const ClearableDemo: React.FC = () => {
  const [value, setValue] = useState<string>('');

  return (
    <div className="dbi-component-select-demo__inline">
      <Select
        label="可清除"
        value={value}
        onChange={setValue}
        clearable
        placeholder="请选择..."
        options={[
          { value: 'option1', text: '选项一' },
          { value: 'option2', text: '选项二' },
          { value: 'option3', text: '选项三' },
        ]}
      />
      <span className="dbi-component-select-demo__value">当前值: {value || '(空)'}</span>
    </div>
  );
};

// 不同尺寸演示
const SizeDemo: React.FC = () => {
  const [smallValue, setSmallValue] = useState<string>('option1');
  const [mediumValue, setMediumValue] = useState<string>('option1');
  const [largeValue, setLargeValue] = useState<string>('option1');

  return (
    <div className="dbi-component-select-demo__row">
      <Select
        label="小尺寸"
        size="s"
        value={smallValue}
        onChange={setSmallValue}
        options={[
          { value: 'option1', text: 'Option 1' },
          { value: 'option2', text: 'Option 2' },
        ]}
      />
      <Select
        label="中尺寸(默认)"
        size="m"
        value={mediumValue}
        onChange={setMediumValue}
        options={[
          { value: 'option1', text: 'Option 1' },
          { value: 'option2', text: 'Option 2' },
        ]}
      />
      <Select
        label="大尺寸"
        size="l"
        value={largeValue}
        onChange={setLargeValue}
        options={[
          { value: 'option1', text: 'Option 1' },
          { value: 'option2', text: 'Option 2' },
        ]}
      />
    </div>
  );
};

// 不显示 label 演示
const NoLabelDemo: React.FC = () => {
  const [value, setValue] = useState<string>('option1');

  return (
    <Select
      value={value}
      onChange={setValue}
      isShowLabel={false}
      options={[
        { value: 'option1', text: '选项一' },
        { value: 'option2', text: '选项二' },
        { value: 'option3', text: '选项三' },
      ]}
    />
  );
};

// 自定义气泡宽度演示
const TooltipWidthDemo: React.FC = () => {
  const [value, setValue] = useState<string>('option1');

  return (
    <Select
      label="提示宽度"
      value={value}
      onChange={setValue}
      tooltipMaxWidth={300}
      options={[
        {
          value: 'option1',
          text: '选项一',
          tooltip: '这是一个较长的气泡提示内容，用来测试自定义宽度效果。可以设置数字或字符串。',
        },
        {
          value: 'option2',
          text: '选项二',
          tooltip: '另一个较长的提示内容，展示气泡宽度限制的效果。',
        },
      ]}
    />
  );
};

// 图标为图片 URL 演示
const ImageIconDemo: React.FC = () => {
  const [value, setValue] = useState<string>('sensortower');

  return (
    <Select
      label="图片图标"
      value={value}
      onChange={setValue}
      options={[
        {
          value: 'sensortower',
          text: 'Sensor Tower',
          icon: 'https://via.placeholder.com/16/4A90E2/FFFFFF?text=S',
          tooltip: '使用图片URL作为图标',
        },
        {
          value: 'appannie',
          text: 'data.ai',
          icon: 'https://via.placeholder.com/16/E24A4A/FFFFFF?text=D',
          tooltip: '另一个图片URL图标',
        },
      ]}
    />
  );
};

// 自定义按钮渲染演示
const CustomButtonDemo: React.FC = () => {
  const [value, setValue] = useState<string>('option1');

  return (
    <div className="dbi-component-select-demo__inline">
      <Select
        value={value}
        onChange={setValue}
        options={[
          { value: 'option1', text: '选项一' },
          { value: 'option2', text: '选项二' },
          { value: 'option3', text: '选项三' },
        ]}
        button={(selectedOption) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 12px' }}>
            <span style={{ color: '#1890ff' }}>🔍</span>
            <span>自定义: {selectedOption?.text || '请选择'}</span>
          </div>
        )}
      />
      <span className="dbi-component-select-demo__value">当前值: {value}</span>
    </div>
  );
};

// 重复选择触发回调演示
const DistinctDemo: React.FC = () => {
  const [value, setValue] = useState<string>('option1');
  const [triggerCount, setTriggerCount] = useState<number>(0);

  const handleChange = (val: string) => {
    setValue(val);
    setTriggerCount((prev) => prev + 1);
  };

  return (
    <div className="dbi-component-select-demo__inline">
      <Select
        label="允许重复触发"
        value={value}
        onChange={handleChange}
        isDistinct={false}
        options={[
          { value: 'option1', text: '选项一' },
          { value: 'option2', text: '选项二' },
          { value: 'option3', text: '选项三' },
        ]}
      />
      <span className="dbi-component-select-demo__value">回调触发次数: {triggerCount}</span>
    </div>
  );
};

// 搜索功能演示
const SearchableDemo: React.FC = () => {
  const [value, setValue] = useState<string>('');

  return (
    <div className="dbi-component-select-demo__inline">
      <Select
        label="搜索选择"
        value={value}
        onChange={setValue}
        searchable
        searchPlaceholder="输入关键字搜索"
        options={[
          { value: 'apple', text: 'Apple' },
          { value: 'banana', text: 'Banana' },
          { value: 'cherry', text: 'Cherry' },
          { value: 'durian', text: 'Durian' },
          { value: 'elderberry', text: 'Elderberry' },
        ]}
      />
      <span className="dbi-component-select-demo__value">当前值: {value || '(空)'}</span>
    </div>
  );
};

// 删除选项功能演示
const DeletableDemo: React.FC = () => {
  const [value, setValue] = useState<string>('');
  const [options, setOptions] = useState([
    { value: 'preset1', text: '高收入游戏筛选方案' },
    { value: 'preset2', text: '休闲品类分析方案' },
    { value: 'preset3', text: '美国市场数据方案' },
  ]);

  return (
    <div className="dbi-component-select-demo__inline">
      <Select
        label="可删除选项"
        value={value}
        onChange={setValue}
        deletable
        options={options}
        onDelete={(deleteValue) => {
          setOptions(options.filter((opt) => opt.value !== deleteValue));
        }}
      />
      <span className="dbi-component-select-demo__value">剩余选项: {options.length}</span>
    </div>
  );
};

// Footer 提示演示
const FooterDemo: React.FC = () => {
  const [value, setValue] = useState<string>('');

  const manyOptions = Array.from({ length: 15 }, (_, i) => ({
    value: `option${i + 1}`,
    text: `选项 ${i + 1}`,
  }));

  return (
    <Select
      label="超过10条提示"
      value={value}
      onChange={setValue}
      searchable
      maxDisplayCount={10}
      options={manyOptions}
    />
  );
};

// 搜索 + 清空 + 删除组合演示
const FullFeaturedDemo: React.FC = () => {
  const [value, setValue] = useState<string>('');
  const [options, setOptions] = useState([
    { value: 'preset1', text: '高收入游戏筛选方案' },
    { value: 'preset2', text: '休闲品类分析方案' },
    { value: 'preset3', text: '美国市场数据方案' },
  ]);

  return (
    <Select
      label="全功能"
      value={value}
      onChange={setValue}
      searchable
      clearable
      deletable
      options={options}
      onDelete={(deleteValue) => {
        setOptions(options.filter((opt) => opt.value !== deleteValue));
      }}
      searchPlaceholder="搜索方案"
      deleteConfirmTitle="确认删除"
      deleteConfirmDesc="删除后将无法恢复，确定要删除此方案吗？"
    />
  );
};

/**
 * Demo 案例配置
 * 每个配置项对应文档页面中的一个演示场景
 */
export const demoConfig = [
  {
    title: '基本使用',
    description: '最基本的下拉选择组件用法',
    code: `<Select
  label="Source"
  value={value}
  onChange={setValue}
  options={[
    { value: 'option1', text: 'Option 1' },
    { value: 'option2', text: 'Option 2' },
    { value: 'option3', text: 'Option 3' },
  ]}
/>`,
    element: <BasicDemo />,
  },
  {
    title: '带图标和气泡提示',
    description: '选项可以配置图标和气泡提示，帮助用户理解选项含义',
    code: `<Select
  label="数据源"
  value={value}
  onChange={setValue}
  tooltipMaxWidth={200}
  options={[
    {
      value: 'sensortower',
      text: 'Sensor Tower',
      icon: <SensorTowerIcon />,
      tooltip: 'Sensor Tower 数据源',
    },
    {
      value: 'appannie',
      text: 'data.ai',
      icon: <AppAnnieIcon />,
      tooltip: 'data.ai 数据源',
    },
  ]}
/>`,
    element: <IconDemo />,
  },
  {
    title: '支持清除已选项（clearable）',
    description: '设置 clearable 属性后，可以清除已选择的值',
    code: `<Select
  label="可清除"
  value={value}
  onChange={setValue}
  clearable
  placeholder="请选择..."
  options={[
    { value: 'option1', text: '选项一' },
    { value: 'option2', text: '选项二' },
    { value: 'option3', text: '选项三' },
  ]}
/>`,
    element: <ClearableDemo />,
  },
  {
    title: '不同尺寸',
    description: '支持 small(s)、medium(m)、large(l) 三种尺寸',
    code: `<Select label="小尺寸" size="s" {...props} />
<Select label="中尺寸(默认)" size="m" {...props} />
<Select label="大尺寸" size="l" {...props} />`,
    element: <SizeDemo />,
  },
  {
    title: '不显示label（isShowLabel=false）',
    description: '当不需要显示 label 时，可以设置 isShowLabel={false}',
    code: `<Select
  value={value}
  onChange={setValue}
  isShowLabel={false}
  options={[
    { value: 'option1', text: '选项一' },
    { value: 'option2', text: '选项二' },
    { value: 'option3', text: '选项三' },
  ]}
/>`,
    element: <NoLabelDemo />,
  },
  {
    title: '禁用状态',
    description: '禁用状态下不可选择',
    code: `<Select
  label="禁用"
  value="option1"
  disabled
  options={[
    { value: 'option1', text: '选项一' },
    { value: 'option2', text: '选项二' },
  ]}
/>`,
    element: (
      <Select
        label="禁用"
        value="option1"
        disabled
        options={[
          { value: 'option1', text: '选项一' },
          { value: 'option2', text: '选项二' },
        ]}
      />
    ),
  },
  {
    title: '自定义气泡宽度（tooltipWidth）',
    description: '可以自定义选项气泡提示的宽度',
    code: `<Select
  label="提示宽度"
  value={value}
  onChange={setValue}
  tooltipMaxWidth={300}
  options={[
    {
      value: 'option1',
      text: '选项一',
      tooltip: '这是一个较长的气泡提示内容...',
    },
  ]}
/>`,
    element: <TooltipWidthDemo />,
  },
  {
    title: '图标为图片URL',
    description: '选项图标可以使用图片 URL',
    code: `<Select
  label="图片图标"
  value={value}
  onChange={setValue}
  options={[
    {
      value: 'sensortower',
      text: 'Sensor Tower',
      icon: 'https://via.placeholder.com/16/4A90E2/FFFFFF?text=S',
      tooltip: '使用图片URL作为图标',
    },
  ]}
/>`,
    element: <ImageIconDemo />,
  },
  {
    title: '自定义按钮渲染（button）',
    description: '使用 button 属性可完全自定义按钮渲染内容',
    code: `<Select
  value={value}
  onChange={setValue}
  options={[
    { value: 'option1', text: '选项一' },
    { value: 'option2', text: '选项二' },
  ]}
  button={(selectedOption) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span>🔍</span>
      <span>自定义: {selectedOption?.text || '请选择'}</span>
    </div>
  )}
/>`,
    element: <CustomButtonDemo />,
  },
  {
    title: '重复选择触发回调（isDistinct=false）',
    description: '默认情况下重复选择同一选项不会触发onChange，设置 isDistinct={false} 可允许重复触发',
    code: `<Select
  label="允许重复触发"
  value={value}
  onChange={handleChange}
  isDistinct={false}
  options={[
    { value: 'option1', text: '选项一' },
    { value: 'option2', text: '选项二' },
    { value: 'option3', text: '选项三' },
  ]}
/>`,
    element: <DistinctDemo />,
  },
  {
    title: '搜索功能（searchable）',
    description: '设置 searchable 属性后，可以搜索筛选选项',
    code: `<Select
  label="搜索选择"
  value={value}
  onChange={setValue}
  searchable
  searchPlaceholder="输入关键字搜索"
  options={[...]}
/>`,
    element: <SearchableDemo />,
  },
  {
    title: '删除选项功能（deletable）',
    description: '设置 deletable 属性后，hover 选项时显示删除图标，点击后弹出确认弹窗',
    code: `<Select
  label="可删除选项"
  value={value}
  onChange={setValue}
  deletable
  options={options}
  onDelete={(value) => {
    // 从列表中移除该选项
  }}
/>`,
    element: <DeletableDemo />,
  },
  {
    title: '超过数量显示提示（maxDisplayCount）',
    description: '设置 maxDisplayCount 后，选项超过该数量时在底部显示提示',
    code: `<Select
  label="超过10条提示"
  value={value}
  onChange={setValue}
  searchable
  maxDisplayCount={10}
  options={manyOptions}
/>`,
    element: <FooterDemo />,
  },
  {
    title: '全功能组合',
    description: '搜索 + 清空 + 删除功能的完整组合',
    code: `<Select
  label="全功能"
  value={value}
  onChange={setValue}
  searchable
  clearable
  deletable
  options={options}
  onDelete={(value) => {
    // 从列表中移除该选项
  }}
  searchPlaceholder="搜索方案"
  deleteConfirmTitle="确认删除"
  deleteConfirmDesc="删除后将无法恢复，确定要删除此方案吗？"
/>`,
    element: <FullFeaturedDemo />,
  },
];

export default demoConfig;
